import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCoils, deleteCoil, exportCsvUrl, listMasters, releaseHoldCoil } from "@/lib/api";
import { fmtCoilKg } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Download, Trash2, Loader2, FilterX, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import EditRMDialog from "@/components/EditRMDialog";
import EditSFGDialog from "@/components/EditSFGDialog";
import EditFGDialog from "@/components/EditFGDialog";
import CoilTreeDialog from "@/components/CoilTreeDialog";
import PrintButton from "@/components/PrintButton";
import { SfgWeightFilter } from "@/components/SfgWeightFilter";
import { singleLabelPdfUrl, coilLabelTsplUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const Inventory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [grade, setGrade] = useState("__all__");
  const [supplier, setSupplier] = useState("__all__");
  const [status, setStatus] = useState("__all__");
  const [kind, setKind] = useState(() => {
    const k = searchParams.get("kind");
    return ["mother", "sfg", "fg"].includes(k) ? k : "__all__";
  });
  const [sfgWidth, setSfgWidth] = useState("");
  const [sfgGrade, setSfgGrade] = useState("__all__");
  const [sfgOrigin, setSfgOrigin] = useState("all");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editCoil, setEditCoil] = useState(null);
  const [treeCoilId, setTreeCoilId] = useState(null);
  const queryClient = useQueryClient();
  const { user, hasModule } = useAuth();
  const canEdit = user?.role === "director" || user?.role === "dispatch_executive";
  const canDelete = user?.role === "director";
  const isDirector = user?.role === "director";
  const releaseMut = useMutation({
    mutationFn: releaseHoldCoil,
    onSuccess: (c) => {
      toast.success(c.bundle_id ? `Bundle ${c.bundle_id} released — all its coils now Received` : `${c.coil_id} released — now Received`);
      queryClient.invalidateQueries({ queryKey: ["coils"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't release"),
  });

  // Keep URL and kind filter in sync so deep-links like /inventory?kind=fg are shareable.
  useEffect(() => {
    const current = searchParams.get("kind") || "";
    const next = kind === "__all__" ? "" : kind;
    if (current !== next) {
      const params = new URLSearchParams(searchParams);
      if (next) params.set("kind", next); else params.delete("kind");
      setSearchParams(params, { replace: true });
    }
  }, [kind, searchParams, setSearchParams]);

  const { data: coils = [], isLoading } = useQuery({
    queryKey: ["coils"],
    queryFn: () => fetchCoils(),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return coils.filter((c) => {
      if (q) {
        const blob = `${c.coil_id} ${c.grade} ${c.supplier} ${c.supplier_id || ""} ${c.notes || ""} ${c.mother_coil_id || ""} ${c.sku_name || ""}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (grade !== "__all__" && c.grade !== grade) return false;
      if (supplier !== "__all__" && c.supplier !== supplier) return false;
      if (kind !== "__all__" && (c.kind || "mother") !== kind) return false;
      if (status !== "__all__" && c.status !== status) return false;
      if (kind === "sfg") {
        const w = parseFloat(sfgWidth);
        if (Number.isFinite(w) && Math.abs(Number(c.width_mm || 0) - w) > 0.001) return false;
        if (sfgGrade !== "__all__" && c.grade !== sfgGrade) return false;
        if (sfgOrigin === "purchased" && !c.packaging_list_id) return false;
        if (sfgOrigin === "received" && c.packaging_list_id) return false;
      }
      return true;
    });
  }, [coils, search, grade, supplier, kind, status, sfgWidth, sfgGrade, sfgOrigin]);

  const gradeOptions = useMemo(() => Array.from(new Set(coils.map((c) => c.grade))).filter(Boolean).sort(), [coils]);
  const sfgWidthOptions = useMemo(() => Array.from(new Set(coils
    .filter((c) => c.kind === "sfg" && (status === "__all__" || c.status === status) && (sfgGrade === "__all__" || c.grade === sfgGrade))
    .map((c) => Number(c.width_mm)).filter((w) => w > 0))).sort((a, b) => a - b), [coils, status, sfgGrade]);
  const sfgGradeOptions = useMemo(() => Array.from(new Set(coils.filter((c) => c.kind === "sfg").map((c) => c.grade))).filter(Boolean).sort(), [coils]);

  // FG rows show the RM Grade master Name (e.g. "50C600") for their short grade (e.g. "600").
  const { data: rmGrades = [] } = useQuery({
    queryKey: ["masters", "rm_grade", "active", "full"],
    queryFn: () => listMasters({ master_type: "rm_grade", status: "active" }),
    staleTime: 60_000,
  });
  const fgGradeName = (c) => {
    const g = String(c.grade || "").trim().toLowerCase();
    if (!g) return "";
    const matches = rmGrades
      .filter((m) => String(m.meta?.grade || "").trim().toLowerCase() === g)
      .sort((a, b) => a.name.localeCompare(b.name));
    const best = matches.find((m) => Number(m.meta?.thickness_mm) === Number(c.thickness_mm)) || matches[0];
    return best ? best.name : c.grade;
  };
  const supplierOptions = useMemo(() => Array.from(new Set(coils.map((c) => c.supplier))).filter(Boolean).sort(), [coils]);

  const totalWeight = filtered.reduce((s, c) => s + (c.weight_kg || 0), 0);

  const clearFilters = () => {
    setSearch("");
    setGrade("__all__");
    setSupplier("__all__");
    setKind("__all__");
    setStatus("__all__");
    setSfgWidth("");
    setSfgGrade("__all__");
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteCoil(confirmDelete.id);
      toast.success(`Coil ${confirmDelete.coil_id} removed`);
      queryClient.invalidateQueries({ queryKey: ["coils"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    } catch (e) {
      toast.error("Failed to delete coil");
    } finally {
      setConfirmDelete(null);
    }
  };

  // Kind → human label used in the delete-confirm button ("Remove RM" etc.).
  const kindLabel = (k) => (k === "sfg" ? "SFG" : k === "fg" ? "FG" : "RM");
  const editKind = (editCoil?.kind || "mother");
  const deleteKind = (confirmDelete?.kind || "mother");

  return (
    <div data-testid="inventory-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-1">Catalog</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Inventory
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            <span className="font-mono-data font-semibold text-slate-900" data-testid="filtered-count">
              {filtered.length}
            </span>{" "}
            of {coils.length} coils ·{" "}
            <span className="font-mono-data font-semibold text-slate-900">
              {(totalWeight / 1000).toFixed(2)} t
            </span>{" "}
            displayed
          </p>
        </div>
        <a
          href={exportCsvUrl()}
          data-testid="export-csv-btn"
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 h-10 rounded-sm tracking-wide transition-colors"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </a>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="search-input"
            placeholder="Search coil ID, supplier, grade, RM…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid="filter-kind" className="h-10">
            <SelectValue placeholder="All kinds" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All kinds</SelectItem>
            <SelectItem value="mother">RM</SelectItem>
            <SelectItem value="sfg">SFG</SelectItem>
            <SelectItem value="fg">FG</SelectItem>
          </SelectContent>
        </Select>
        <Select value={grade} onValueChange={setGrade}>
          <SelectTrigger data-testid="filter-grade" className="h-10">
            <SelectValue placeholder="All grades" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All grades</SelectItem>
            {gradeOptions.map((g) => (
              <SelectItem key={g} value={g}>{g}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={supplier} onValueChange={setSupplier}>
          <SelectTrigger data-testid="filter-supplier" className="h-10">
            <SelectValue placeholder="All suppliers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All suppliers</SelectItem>
            {supplierOptions.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="filter-status" className="h-10">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__" data-testid="filter-status-all">Status</SelectItem>
            {[["pending", "Pending"], ["slitting_pending", "Slitting Pending"], ["received", "Received"], ["received_hold", "Received – Hold"], ["dispatched", "Dispatched"], ["consumed", "Consumed"]].map(([s, label]) => (
              <SelectItem key={s} value={s} data-testid={`filter-status-${s}`}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {kind === "sfg" && (
          <div className="md:col-span-6 flex items-center gap-2 flex-wrap" data-testid="sfg-origin-tabs">
            <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mr-1">SFG category</span>
            {[
              ["all", "All (Received + Purchased)"],
              ["received", "Received · slit in-house"],
              ["purchased", "Purchased · from supplier"],
            ].map(([v, label]) => (
              <button key={v} type="button" onClick={() => setSfgOrigin(v)} data-testid={`sfg-origin-${v}`}
                className={`h-8 px-3 rounded-sm text-xs font-semibold border transition-colors ${sfgOrigin === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                {label}
              </button>
            ))}
          </div>
        )}
        {kind === "sfg" && (
          <SfgWeightFilter
            width={sfgWidth} setWidth={setSfgWidth} widths={sfgWidthOptions}
            grade={sfgGrade} setGrade={setSfgGrade}
            grades={sfgGradeOptions}
            count={filtered.length} totalKg={totalWeight}
          />
        )}
        <div className="md:col-span-6 flex justify-end">
          <Button
            variant="ghost"
            data-testid="clear-filters-btn"
            onClick={clearFilters}
            className="text-slate-600 h-9"
          >
            <FilterX className="h-4 w-4 mr-2" />
            Clear filters
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {isLoading ? (
          <div className="h-64 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-state" className="h-64 flex flex-col items-center justify-center text-center px-6">
            <p className="font-heading text-base font-semibold text-slate-900">No coils found</p>
            <p className="text-sm text-slate-500 mt-1">
              {coils.length === 0
                ? "Scan your first coil to populate the inventory."
                : "Try adjusting the filters."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50 sticky top-0">
                <TableRow>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">
                    {kind === "mother" ? "SR RM ID" : kind === "sfg" ? "SR SFG ID" : kind === "fg" ? "SR FG ID" : "SR ID"}
                  </TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Kind</TableHead>
                  {kind === "fg" && (
                    <TableHead data-testid="col-sku" className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">SKU</TableHead>
                  )}
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Grade</TableHead>
                  <TableHead className="text-right text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Weight</TableHead>
                  <TableHead className="text-right text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Thickness</TableHead>
                  <TableHead className="text-right text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Width</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Supplier</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Supplier ID</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Received</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Status</TableHead>
                  <TableHead className="text-right text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow
                    key={c.id}
                    data-testid={`coil-row-${c.coil_id}`}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <TableCell className="font-mono-data font-semibold text-slate-900">
                      <button
                        type="button"
                        data-testid={`open-tree-${c.coil_id}`}
                        onClick={() => setTreeCoilId(c.coil_id)}
                        className="text-left hover:text-blue-700 hover:underline transition-colors"
                        title="Show slit tree"
                      >
                        {c.coil_id}
                      </button>
                      {c.kind === "fg" && c.needs_review && (
                        <span data-testid={`needs-review-${c.coil_id}`} title={`Needs review:\n${(c.review_reasons || []).join("\n")}\nEdit this FG to correct it.`}
                          className="ml-1.5 inline-flex align-middle cursor-help">
                          <AlertTriangle className="h-4 w-4 text-amber-500 fill-amber-100" />
                        </span>
                      )}
                      {c.kind === "sfg" && c.mother_coil_id && (
                        <div className="text-[10px] text-slate-500 font-normal mt-0.5">
                          from <span className="font-mono-data">{c.mother_coil_id}</span>
                          {c.sku_name && (
                            <>
                              {" · "}
                              <span className="font-mono-data text-slate-700">{c.sku_name}</span>
                            </>
                          )}
                        </div>
                      )}
                      {c.kind === "fg" && (
                        <div className="text-[10px] text-slate-500 font-normal mt-0.5 space-y-0.5">
                          {c.sku_name && kind !== "fg" && <div className="font-mono-data">{c.sku_name}</div>}
                          {c.parent_coil_id && (
                            <div>split from <span className="font-mono-data">{c.parent_coil_id}</span></div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {(c.kind || "mother") === "sfg" ? (
                        <>
                        <span data-testid={`kind-${c.coil_id}`} className="inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm bg-orange-50 text-orange-800 border border-orange-200">SFG</span>
                        {c.packaging_list_id && (
                          <span data-testid={`purchased-tag-${c.coil_id}`} title={`Bought from ${c.supplier || "supplier"}${c.packaging_list_number ? ` · packing list ${c.packaging_list_number}` : ""}`} className="ml-1 inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-orange-100 text-orange-800 border border-orange-300">
                            Purchased
                          </span>
                        )}
                        {c.sfg_count > 1 && (
                          <span data-testid={`sfg-count-${c.coil_id}`} title={`This record holds ${c.sfg_count} SFGs`} className="ml-1 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-orange-600 text-white whitespace-nowrap">× {c.sfg_count} SFGs</span>
                        )}
                        </>
                      ) : (c.kind || "mother") === "fg" ? (
                        <span data-testid={`kind-${c.coil_id}`} className="inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200">FG</span>
                      ) : (
                        <span data-testid={`kind-${c.coil_id}`} className="inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700 border border-slate-200">RM</span>
                      )}
                    </TableCell>
                    {kind === "fg" && (
                      <TableCell data-testid={`sku-${c.coil_id}`} className="font-mono-data font-semibold text-slate-800 whitespace-nowrap">
                        {c.sku_name || "—"}
                      </TableCell>
                    )}
                    <TableCell data-testid={`grade-${c.coil_id}`}>
                      {(c.kind === "fg" ? fgGradeName(c) : c.grade) || "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono-data whitespace-nowrap" data-testid={`weight-${c.coil_id}`}>
                      {fmtCoilKg(c.kind, c.weight_kg)} kg
                    </TableCell>
                    <TableCell className="text-right font-mono-data">{c.thickness_mm != null ? `${c.thickness_mm} mm` : "—"}</TableCell>
                    <TableCell className="text-right font-mono-data">{c.width_mm != null ? `${c.width_mm} mm` : "—"}</TableCell>
                    <TableCell>{c.supplier || "—"}</TableCell>
                    <TableCell className="font-mono-data">{c.supplier_id || "—"}</TableCell>
                    <TableCell className="text-slate-600">{c.date_received || "—"}</TableCell>
                    <TableCell>
                      {c.status === "pending" ? (
                        <span data-testid={`status-${c.coil_id}`} className="inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200">
                          Pending
                        </span>
                      ) : c.status === "consumed" ? (
                        <span data-testid={`status-${c.coil_id}`} className="inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200">
                          Consumed
                        </span>
                      ) : c.status === "slitting_pending" ? (
                        <span data-testid={`status-${c.coil_id}`} className="inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm bg-violet-50 text-violet-800 border border-violet-200 whitespace-nowrap">
                          Slitting Pending
                        </span>
                      ) : c.status === "received_hold" ? (
                        <span className="inline-flex items-center gap-1">
                          <span data-testid={`status-${c.coil_id}`} title={c.bundle_id ? `Bundle ${c.bundle_id} · crane ${c.bundle_crane_gross_kg} kg · diff ${c.weigh_diff_kg > 0 ? "+" : ""}${c.weigh_diff_kg} kg (whole bundle)` : c.weigh_diff_kg != null ? `Crane ${c.crane_gross_kg} kg · diff ${c.weigh_diff_kg > 0 ? "+" : ""}${c.weigh_diff_kg} kg` : ""}
                            className="inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm bg-red-600 text-white border border-red-700 whitespace-nowrap">
                            Received – Hold
                          </span>
                          {c.bundle_id && (
                            <span data-testid={`bundle-tag-${c.coil_id}`} className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-800 border border-indigo-200">{c.bundle_id}</span>
                          )}
                          {isDirector && (
                            <button data-testid={`release-hold-${c.coil_id}`} onClick={() => releaseMut.mutate(c.coil_id)} disabled={releaseMut.isPending}
                              title={c.bundle_id ? `Releases every coil in bundle ${c.bundle_id}` : ""}
                              className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-green-600 text-green-700 hover:bg-green-50 disabled:opacity-50">
                              {c.bundle_id ? "Release bundle" : "Release"}
                            </button>
                          )}
                        </span>
                      ) : c.status === "dispatched" ? (
                        <span data-testid={`status-${c.coil_id}`} className="inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm bg-blue-50 text-blue-800 border border-blue-200">
                          Dispatched
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <span data-testid={`status-${c.coil_id}`} className={`inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm border whitespace-nowrap ${c.kind === "sfg" && c.packaging_list_id ? "bg-orange-50 text-orange-800 border-orange-300" : "bg-green-50 text-green-800 border-green-200"}`}>
                            {c.kind === "sfg" && c.packaging_list_id ? "Received – Purchased" : "Received"}
                          </span>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <PrintButton
                          variant="icon"
                          tspl={coilLabelTsplUrl(c.id)}
                          pdf={singleLabelPdfUrl(c.id)}
                          label={`${c.coil_id} label`}
                          testId={`print-label-${c.coil_id}`}
                        />
                        {(c.kind === "fg" ? hasModule("inventory.fg_edit") : canEdit) && (
                          <button
                            data-testid={`edit-coil-${c.coil_id}`}
                            onClick={() => setEditCoil(c)}
                            className="text-slate-400 hover:text-slate-900 transition-colors p-1.5"
                            aria-label="Edit coil"
                            title="Edit coil"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        {(c.kind === "fg" ? hasModule("inventory.fg_delete") : canDelete) && (
                          <button
                            data-testid={`delete-coil-${c.coil_id}`}
                            onClick={() => setConfirmDelete(c)}
                            className="text-slate-400 hover:text-red-600 transition-colors p-1.5"
                            aria-label="Delete coil"
                            title="Delete coil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.coil_id}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the coil from inventory. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove {kindLabel(deleteKind)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editCoil && editKind === "sfg" ? (
        <EditSFGDialog
          coil={editCoil}
          open={!!editCoil}
          onOpenChange={(o) => !o && setEditCoil(null)}
        />
      ) : editCoil && editKind === "fg" ? (
        <EditFGDialog
          coil={editCoil}
          open={!!editCoil}
          onOpenChange={(o) => !o && setEditCoil(null)}
        />
      ) : editCoil ? (
        <EditRMDialog
          coil={editCoil}
          open={!!editCoil}
          onOpenChange={(o) => !o && setEditCoil(null)}
        />
      ) : null}

      <CoilTreeDialog
        open={!!treeCoilId}
        onOpenChange={(o) => !o && setTreeCoilId(null)}
        coilId={treeCoilId}
      />
    </div>
  );
};

export default Inventory;
